/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import type { UnitDescribe } from '@g17eco/convert-units';
import React from 'react';
import { Input, InputGroupText } from 'reactstrap';
import { BaseInputProps } from './InputProps';
import { getUnitsForType, UnitTypes } from '@utils/units';
import { UtrvStatus } from '@constants/status';
import { TableColumn } from '@g17eco/types/universalTracker';

type UnitDescribeExtended = UnitDescribe & { disabled?: boolean };

const staticSuffix = ({
  suffix,
  disabled,
  suffixClass = '',
  disabledTooltip,
}: {
  suffix: string;
  disabled?: boolean;
  suffixClass?: string;
  disabledTooltip?: string;
}) => {
  return (
    <InputGroupText className={`${suffixClass} ${disabled ? 'disabled' : ''} px-4`} title={disabledTooltip}>
      {suffix}
    </InputGroupText>
  );
};

interface InputGroupCurrencyProps extends Pick<BaseInputProps, 'isDisabled' | 'numberScale' | 'handleNumberScaleChange' | 'isInvalid'> {
  options: UnitDescribeExtended[];
  disabledTooltip?: string;
}

export function genericInputGroupNumberScale({
  isDisabled,
  numberScale,
  options,
  handleNumberScaleChange,
  isInvalid,
  disabledTooltip,
}: InputGroupCurrencyProps) {
  const disabled = isDisabled?.();
  const className = isInvalid ? 'styled-readonly-invalid' : 'styled-readonly';

  if (!numberScale) {
    return <></>;
  }

  if (disabled) {
    const op = options.find((u) => u.abbr === numberScale);
    if (op) {
      return staticSuffix({ suffix: op.plural, disabled, suffixClass: className, disabledTooltip });
    }
    return staticSuffix({ suffix: '', disabled, suffixClass: className, disabledTooltip });
  }

  return (
    <Input
      type='select'
      disabled={disabled}
      onChange={(e) => handleNumberScaleChange(e.target.value)}
      value={numberScale || ''}
      className={`${className} flex-grow-0 w-25`}
    >
      {options.map((u) => (
        <option key={u.abbr} value={u.abbr} title={u.disabled ? 'No longer available' : ''} disabled={u.disabled}>
          {u.plural}
        </option>
      ))}
    </Input>
  );
}

const getValueOptionText = (value: number | string | string[] | undefined, op: UnitDescribe) => Number(value) === 1 ? op.singular : op.plural;

export interface GenericInputGroupProps extends Pick<BaseInputProps, 'isDisabled' | 'unit' | 'suffix' | 'isInvalid'> {
  options?: UnitDescribeExtended[],
  value?: number | string | string[],
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void,
  testId?: string;
  disabledTooltip?: string;
}

export interface InputGroupProps
  extends GenericInputGroupProps,
    Pick<
      BaseInputProps,
      'universalTracker' | 'numberScale' | 'handleNumberScaleChange' | 'isInvalid' | 'status' | 'initiativeUtr'
    > {
  column?: Pick<TableColumn, 'code'>;
}

export function genericInputGroup({ options, value, isDisabled, unit, handleChange, suffix, isInvalid, testId, disabledTooltip }: GenericInputGroupProps) {
  const disabled = isDisabled?.();
  const className = isInvalid ? 'styled-readonly-invalid' : 'styled-readonly';
  if (options && options.length > 0) {
    if (disabled) {
      if (unit) {
        const op = options.find((u) => u.abbr === unit);
        if (op) {
          return staticSuffix({
            suffix: getValueOptionText(value, op),
            disabled,
            suffixClass: className,
            disabledTooltip,
          });
        }
      }
      return staticSuffix({ suffix: '', disabled, suffixClass: className, disabledTooltip });
    }

    return (
      <Input
        type='select'
        data-testid={testId}
        disabled={disabled}
        onChange={handleChange}
        value={unit || ''}
        className={`${className} flex-grow-0 w-25`}
      >
        {options.map((op) => (
          <option key={op.abbr} value={op.abbr} title={op.disabled ? 'No longer available' : ''} disabled={op.disabled}>
            {`${getValueOptionText(value, op)} (${op.abbr})`}
          </option>
        ))}
      </Input>
    );
  }

  if (suffix) {
    return staticSuffix({ suffix, disabled, suffixClass: className, disabledTooltip });
  }

  return '';
}

export function renderInputGroup(props: InputGroupProps) {
  const {
    universalTracker,
    suffix,
    unit,
    value,
    numberScale,
    handleChange,
    handleNumberScaleChange,
    isInvalid,
    initiativeUtr,
    column,
  } = props;

  const { unitInput, unitLocked, numberScaleInput, numberScaleLocked } = column
    ? initiativeUtr?.valueValidation?.table?.columns.find((col) => col.code === column.code) ?? {}
    : initiativeUtr ?? {};

  const { options: numberScaleOptions, isDisabled: isNumberScaleDisabled } = getDropdownProps({
    status: props.status,
    isDisabled: props.isDisabled,
    unitType: UnitTypes.numberScale,
    currentValue: numberScale,
    overriddenValue: numberScaleInput,
    isLocked: numberScaleLocked,
  });

  if (universalTracker.isCurrency()) {
    return genericInputGroupNumberScale({
      isDisabled: isNumberScaleDisabled,
      numberScale: numberScale,
      options: numberScaleOptions,
      handleNumberScaleChange: handleNumberScaleChange,
      isInvalid,
    });
  }

  const { options: unitOptions, isDisabled: isUnitDisabled } = getDropdownProps({
    status: props.status,
    isDisabled: props.isDisabled,
    unitType: universalTracker.getMetricUnitType(),
    currentValue: unit,
    overriddenValue: unitInput,
    isLocked: unitLocked,
  });

  return (
    <>
      {genericInputGroupNumberScale({
        isDisabled: isNumberScaleDisabled,
        numberScale: numberScale,
        options: numberScaleOptions,
        handleNumberScaleChange: handleNumberScaleChange,
        isInvalid,
      })}
      {genericInputGroup({
        options: unitOptions,
        isDisabled: isUnitDisabled,
        unit: unit,
        value: value,
        handleChange: handleChange,
        suffix: suffix,
        isInvalid,
      })}
    </>
  );
}

interface DropdownBaseParams extends Pick<InputGroupProps, 'status' | 'isDisabled'> {
  unitType: string | undefined;
  currentValue: string | undefined;
  overriddenValue: string | undefined;
  isLocked: boolean | undefined;
}

export const getDropdownProps = (props: DropdownBaseParams) => {
  const { status, isDisabled, unitType, currentValue, overriddenValue, isLocked } = props;
  const rawOptions: UnitDescribe[] = getUnitsForType(unitType);

  if (!overriddenValue || !isLocked) {
    return { options: rawOptions, isDisabled };
  }

  if (status === UtrvStatus.Created || currentValue === overriddenValue) {
    return { options: rawOptions, isDisabled: () => true };
  }

  const options = rawOptions.reduce((acc, op) => {
    if (op.abbr === currentValue) {
      acc.push({ ...op, disabled: true });
    }
    if (op.abbr === overriddenValue) {
      acc.push(op);
    }
    return acc;
  }, [] as UnitDescribeExtended[]);

  return { options, isDisabled };
};
